import React, { useEffect, useRef } from "react";
import { Box, Typography, Button, Stack, useTheme } from "@mui/material";
import { gsap } from "gsap";
import { CSSPlugin } from "gsap/CSSPlugin";
import { useNavigate } from "react-router-dom";

import { MARKETING_EVENTS, trackMarketingEvent } from "../utils/demoTracking";

// Force CSSPlugin to not get dropped during build
gsap.registerPlugin(CSSPlugin);

// Badges sit a fraction of their own width apart, so the backdrop keeps the same airy rhythm on a
// phone showing 40 small badges and on a 4K screen showing 130 large ones. This is the knob to turn
// if the pattern ever reads as too tight or too sparse.
const GAP_RATIO = 0.6;

/**
 * Work out a badge grid that tiles the hero exactly: whole badges only, the same gap between every
 * badge in both directions, and whatever space is left over split evenly as an outer margin.
 *
 * The column and row counts are floored against a target cell size, so badges end up at or above
 * the target rather than being squeezed, and the cell is square with the tighter axis winning, so a
 * badge is never clipped by the right or bottom edge.
 */
export const computeGrid = (width, height, maxBadges) => {
  const baseTarget = width < 600 ? 68 : width < 900 ? 88 : 108;

  const solve = (target) => {
    const targetCell = target * (1 + GAP_RATIO);
    // Rounded rather than floored: the cell below is capped by both axes anyway, so rounding up to
    // an extra row only shrinks the cell slightly, where flooring would leave a half-cell band of
    // dead space along the bottom.
    let cols = Math.max(1, Math.round(width / targetCell));
    let rows = Math.max(1, Math.round(height / targetCell));

    // The axis that isn't the tighter constraint was still getting its column/row count from the
    // generic target size, not from the cell size the other axis actually settled on. That left a
    // noticeably bigger margin on that looser axis. Re-deriving its count against the real cell
    // size tightens that margin up so the grid hugs both edges evenly, not just one pair of them.
    // Skipped once both axes are already down to a single cell: the container is smaller than one
    // badge, and there's no looser axis left to tighten.
    if (cols > 1 || rows > 1) {
      let cell = Math.min(width / cols, height / rows);
      if (cell === width / cols) {
        rows = Math.max(1, Math.round(height / cell));
      } else {
        cols = Math.max(1, Math.round(width / cell));
      }
    }
    const cell = Math.min(width / cols, height / rows);

    const size = Math.max(1, Math.floor(cell / (1 + GAP_RATIO)));
    // Flooring the gap keeps a whole cell's worth of slack in hand, which becomes the outer margin.
    const gap = Math.max(1, Math.floor(cell - size));
    return { cols, rows, size, gap };
  };

  // Growing the target badge size is how we honour the badge budget: fewer, larger badges rather
  // than a grid that runs past the end of the deck or costs more image decodes than the device
  // should be asked for.
  let target = baseTarget;
  let grid = solve(target);
  while (
    grid.cols * grid.rows > maxBadges &&
    target < Math.max(width, height)
  ) {
    target += 8;
    grid = solve(target);
  }

  const { cols, rows, size, gap } = grid;
  const gridWidth = cols * size + (cols - 1) * gap;
  const gridHeight = rows * size + (rows - 1) * gap;

  return {
    cols,
    rows,
    size,
    gap,
    offsetX: Math.round((width - gridWidth) / 2),
    offsetY: Math.round((height - gridHeight) / 2),
  };
};

const HeroSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const svgContainerRef = useRef(null);
  const badgeElementsRef = useRef([]);
  // Determine background color based on theme mode
  const backgroundColor =
    theme.palette.mode === "dark"
      ? "rgba(0, 0, 0, 0.7)"
      : "rgba(255, 255, 255, 0.7)";

  // Function to shuffle an array (Fisher-Yates shuffle)
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  useEffect(() => {
    // Assuming you have an array of SVG URLs
    const svgURLs = [
      "badges/AmericanBusiness.svg",
      "badges/AmericanCultures.svg",
      "badges/AmericanHeritage.svg",
      "badges/AmericanIndianCulture.svg",
      "badges/AmericanLabor.svg",
      "badges/AnimalScience.svg",
      "badges/Animation.svg",
      "badges/Archaeology.svg",
      "badges/Archery.svg",
      "badges/Architecture.svg",
      "badges/Art.svg",
      "badges/ArtificialIntelligence.svg",
      "badges/Astronomy.svg",
      "badges/Athletics.svg",
      "badges/AutomotiveMaintenance.svg",
      "badges/Aviation.svg",
      "badges/Backpacking.svg",
      "badges/Basketry.svg",
      "badges/BirdStudy.svg",
      "badges/Bugling.svg",
      "badges/Camping.svg",
      "badges/Canoeing.svg",
      "badges/Chemistry.svg",
      "badges/Chess.svg",
      "badges/CitizenshipInSociety.svg",
      "badges/CitizenshipInTheCommunity.svg",
      "badges/CitizenshipInTheNation.svg",
      "badges/CitizenshipInTheWorld.svg",
      "badges/Climbing.svg",
      "badges/CoinCollecting.svg",
      "badges/Collections.svg",
      "badges/Communications.svg",
      "badges/CompetitiveGaming.svg",
      "badges/CompositeMaterials.svg",
      "badges/Cooking.svg",
      "badges/CrimePrevention.svg",
      "badges/Cybersecurity.svg",
      "badges/Cycling.svg",
      "badges/Dentistry.svg",
      "badges/DigitalTechnology.svg",
      "badges/DisabilitiesAwareness.svg",
      "badges/DogCare.svg",
      "badges/Drafting.svg",
      "badges/Electricity.svg",
      "badges/Electronics.svg",
      "badges/EmergencyPreparedness.svg",
      "badges/Energy.svg",
      "badges/Engineering.svg",
      "badges/Entrepreneurship.svg",
      "badges/EnvironmentalScience.svg",
      "badges/Exploration.svg",
      "badges/FamilyLife.svg",
      "badges/FarmMechanics.svg",
      "badges/Fingerprinting.svg",
      "badges/FireSafety.svg",
      "badges/FirstAid.svg",
      "badges/FishAndWildlifeManagement.svg",
      "badges/Fishing.svg",
      "badges/FlyFishing.svg",
      "badges/Forestry.svg",
      "badges/GameDesign.svg",
      "badges/Gardening.svg",
      "badges/Genealogy.svg",
      "badges/Geocaching.svg",
      "badges/Geology.svg",
      "badges/Golf.svg",
      "badges/GraphicArts.svg",
      "badges/HealthcareProfessions.svg",
      "badges/Hiking.svg",
      "badges/HomeRepairs.svg",
      "badges/Horsemanship.svg",
      "badges/IndianLore.svg",
      "badges/InsectStudy.svg",
      "badges/Inventing.svg",
      "badges/Journalism.svg",
      "badges/Kayaking.svg",
      "badges/LandscapeArchitecture.svg",
      "badges/Law.svg",
      "badges/Leatherwork.svg",
      "badges/Lifesaving.svg",
      "badges/MammalStudy.svg",
      "badges/Metalwork.svg",
      "badges/MiningInSociety.svg",
      "badges/ModelDesignAndBuilding.svg",
      "badges/Motorboating.svg",
      "badges/Moviemaking.svg",
      "badges/Multisport.svg",
      "badges/Music.svg",
      "badges/Nature.svg",
      "badges/NuclearScience.svg",
      "badges/Oceanography.svg",
      "badges/Orienteering.svg",
      "badges/Painting.svg",
      "badges/PersonalFitness.svg",
      "badges/PersonalManagement.svg",
      "badges/Pets.svg",
      "badges/Photography.svg",
      "badges/Pioneering.svg",
      "badges/PlantScience.svg",
      "badges/Plumbing.svg",
      "badges/Pottery.svg",
      "badges/Programming.svg",
      "badges/PublicHealth.svg",
      "badges/PublicSpeaking.svg",
      "badges/PulpAndPaper.svg",
      "badges/Radio.svg",
      "badges/Railroading.svg",
      "badges/Reading.svg",
      "badges/ReptileAndAmphibianStudy.svg",
      "badges/RifleShooting.svg",
      "badges/Robotics.svg",
      "badges/Rowing.svg",
      "badges/Safety.svg",
      "badges/Salesmanship.svg",
      "badges/Scholarship.svg",
      "badges/ScoutingHeritage.svg",
      "badges/ScubaDiving.svg",
      "badges/Sculpture.svg",
      "badges/SearchAndRescue.svg",
      "badges/ShotgunShooting.svg",
      "badges/SignsSignalsAndCodes.svg",
      "badges/Skating.svg",
      "badges/SmallBoatSailing.svg",
      "badges/SnowSports.svg",
      "badges/SoilAndWaterConservation.svg",
      "badges/SpaceExploration.svg",
      "badges/Sports.svg",
      "badges/StampCollecting.svg",
      "badges/Surveying.svg",
      "badges/Sustainability.svg",
      "badges/Swimming.svg",
      "badges/Textile.svg",
      "badges/Theater.svg",
      "badges/TrafficSafety.svg",
      "badges/TruckTransportation.svg",
      "badges/VeterinaryMedicine.svg",
      "badges/WaterSports.svg",
      "badges/Weather.svg",
      "badges/Welding.svg",
      "badges/Whitewater.svg",
      "badges/WildernessSurvival.svg",
      "badges/WildlandFireManagement.svg",
      "badges/WoodCarving.svg",
      "badges/Woodwork.svg",
    ];
    // Somebody who has asked their OS to reduce motion should not be met with a hundred-odd badges
    // spiralling in from random depths. They still get the backdrop, just already in place.
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;

    // The deck is shuffled once so a re-layout reuses the badges already on screen instead of
    // dealing a fresh hand every time the viewport nudges.
    const deck = shuffleArray([...svgURLs]);

    let hasAnimated = false;
    let lastWidth = 0;
    let lastHeight = 0;

    const positionSVGs = () => {
      const svgContainer = svgContainerRef.current;
      if (!svgContainer) {
        return;
      }
      const { width: containerWidth, height: containerHeight } =
        svgContainer.getBoundingClientRect();
      if (containerWidth <= 0 || containerHeight <= 0) {
        return;
      }
      if (containerWidth === lastWidth && containerHeight === lastHeight) {
        return;
      }
      lastWidth = containerWidth;
      lastHeight = containerHeight;

      // Only the first layout gets the entrance; everything after is a silent re-fit.
      const animate = !hasAnimated && !reduceMotion;
      hasAnimated = true;

      // A phone gets a smaller share of the full set. At the full deck on a ~390px viewport the
      // individual tiles were too small to read as badges anyway, while still costing an image
      // decode and a concurrent GSAP tween each on the weakest hardware that visits the site, which
      // is the hardware most likely to be on a slow connection and quickest to abandon.
      // The widest tier is the deck itself rather than a literal count, which would otherwise have
      // to be edited in step with the list above and silently drop the newest badges when it wasn't.
      const budget = Math.min(
        containerWidth < 600 ? 40 : containerWidth < 900 ? 80 : deck.length,
        deck.length,
      );
      const { cols, rows, size, gap, offsetX, offsetY } = computeGrid(
        containerWidth,
        containerHeight,
        budget,
      );
      const targetCount = cols * rows;

      const svgs = badgeElementsRef.current;

      while (svgs.length > targetCount) {
        const extra = svgs.pop();
        gsap.killTweensOf(extra);
        extra.remove();
      }
      while (svgs.length < targetCount) {
        const img = new Image();
        img.src = deck[svgs.length];
        img.className = "badge-svg"; // for styling purposes
        // Purely decorative backdrop: empty alt keeps them out of the accessibility
        // tree instead of announcing 138 badge filenames.
        img.alt = "";
        img.setAttribute("aria-hidden", "true");
        svgContainer.appendChild(img);
        svgs.push(img);
      }

      svgs.forEach((svg, index) => {
        const colNum = index % cols;
        const rowNum = Math.floor(index / cols);

        const posX = offsetX + colNum * (size + gap);
        const posY = offsetY + rowNum * (size + gap);

        // Position comes from `left`/`top`; the tween only ever moves the badge relative to that
        // spot, so the grid stays exactly where it was measured.
        svg.style.width = `${size}px`;
        svg.style.height = `${size}px`;
        svg.style.left = `${posX}px`;
        svg.style.top = `${posY}px`;

        if (!animate) {
          gsap.killTweensOf(svg);
          gsap.set(svg, {
            rotationX: 0,
            rotationY: 0,
            rotationZ: 0,
            x: 0,
            y: 0,
            z: 0,
            opacity: 1,
          });
          return;
        }

        // 3D Spiral Animation
        gsap.fromTo(
          svg,
          {
            rotationX: 360 * Math.random(),
            rotationY: 360 * Math.random(),
            rotationZ: 360 * Math.random(),
            x: 100 * Math.cos((2 * Math.PI * index) / svgs.length),
            y: 100 * Math.sin((2 * Math.PI * index) / svgs.length),
            z: -500 + 1000 * Math.random(), // Random depth
            opacity: 0,
          },
          {
            rotationX: 0,
            rotationY: 0,
            rotationZ: 0,
            x: 0,
            y: 0,
            z: 0,
            opacity: 1,
            duration: 2, // animation duration
            ease: "power3.inOut",
            delay: index * 0.02, // Staggered delay for each badge
          },
        );
      });
    };

    // Initial positioning
    positionSVGs();

    /**
     * Re-lay-out whenever the hero's own box changes size, debounced to avoid running mid-drag.
     *
     * A re-layout never replays the entrance animation: on mobile the browser resizes the viewport
     * when the address bar collapses during a scroll, and replaying 138 badges at that moment looked
     * like a bug and stalled the scroll. Instead the existing badges are reused and snapped to the
     * new grid, which is what keeps the bottom row whole when the hero gets shorter.
     */
    let debounce;
    const scheduleRelayout = () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(positionSVGs, 150);
    };

    const container = svgContainerRef.current;
    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(scheduleRelayout)
        : null;
    if (observer && container) {
      observer.observe(container);
    } else {
      window.addEventListener("resize", scheduleRelayout);
    }

    return () => {
      window.clearTimeout(debounce);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleRelayout);
      badgeElementsRef.current.forEach((svg) => {
        gsap.killTweensOf(svg);
        svg.remove();
      });
      badgeElementsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      textAlign="center"
      sx={{
        p: { xs: 2.5, sm: 4 },
        // 70vh of a phone screen is a lot of hero before anything else is reachable, and on iOS `vh`
        // counts the area behind the address bar so it overshoots further still.
        minHeight: { xs: "auto", sm: "70vh" },
        py: { xs: 6, sm: 4 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.palette.background.default,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        ref={svgContainerRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          p: { xs: 2.5, sm: 3 },
          // Slightly more opaque than before so the headline still passes contrast against whichever
          // badges happen to land behind it.
          backgroundColor,
          backdropFilter: "blur(2px)",
          borderRadius: "14px",
          maxWidth: "640px",
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{ color: theme.palette.text.primary }}
        >
          Merit Badge Fundraising, Simplified
        </Typography>
        <Typography
          variant="subtitle1"
          component="p"
          sx={{
            color: theme.palette.text.secondary,
            mt: 2,
            mb: 3,
            fontSize: { xs: "1rem", sm: "1.125rem" },
            lineHeight: 1.6,
          }}
        >
          BadgeReg is registration and fundraising software for Scouting merit
          badge colleges: organize classes, register Scouts, and collect
          payments online for your troop or crew.
        </Typography>
        {/* Two CTAs: the demo for the majority who want to see it before talking to anyone, and a
            direct route to a conversation for the organizer who has already decided. Full-width and
            stacked on a phone so both are comfortable thumb targets. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              trackMarketingEvent(MARKETING_EVENTS.DEMO_CTA_CLICKED);
              navigate("/demo");
            }}
          >
            Try the live demo
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => navigate("/contact")}
          >
            Talk to us
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          No signup required · $250 per event plus $2.50 per Scout · No
          subscription
        </Typography>
      </Box>
    </Box>
  );
};

export default HeroSection;
